import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, RefreshCw, Share2, Trash2, ChefHat, GripVertical, Home as HomeIcon, X, Copy, Check } from "lucide-react";
import { api, RAYONS, RAYON_META, UNITS } from "@/lib/api";
import { LIST_IDS } from "@/constants/testIds/meal";
import ManualEntryModal from "@/components/ManualEntryModal";
import { toast } from "sonner";

function RayonBlock({ rayon, items, onDrop, onDragStart, dragOverRayon, setDragOverRayon, onToggle, onEditQty, onDelete, onChangeUnit, onOpenRecipe }) {
  const meta = RAYON_META[rayon];
  const isOver = dragOverRayon === rayon;

  return (
    <section
      className={`chibi-card p-5 md:p-6 transition-all ${isOver ? "drop-target-over" : ""}`}
      style={{ background: `linear-gradient(180deg, ${meta.bg} 0%, white 60%)` }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOverRayon(rayon);
      }}
      onDragLeave={() => setDragOverRayon(null)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOverRayon(null);
        onDrop(rayon, e);
      }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="text-3xl">{meta.emoji}</div>
        <h3 className="font-display font-semibold text-xl" style={{ color: meta.color }}>
          {rayon}
        </h3>
        <span className="ml-auto text-xs font-soft px-3 py-1 rounded-full bg-white border border-[#E8E2D2]">
          {items.length} article{items.length > 1 ? "s" : ""}
        </span>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-sm text-[#4A3F35]/40 italic border-2 border-dashed border-[#E8E2D2] rounded-[24px]">
          Glisse un ingrédient ici ✨
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <article
              key={it.id}
              draggable
              onDragStart={(e) => onDragStart(it.id, e)}
              className="group flex items-center gap-3 p-3 rounded-[24px] bg-white border border-[#E8E2D2] hover:border-[#A3B18A] cursor-grab active:cursor-grabbing transition-colors"
              data-testid={`list-item-${it.id}`}
            >
              <GripVertical className="w-4 h-4 text-[#4A3F35]/30 group-hover:text-[#4A3F35]/60" />
              <input
                type="checkbox"
                checked={it.checked}
                onChange={(e) => onToggle(it.id, e.target.checked)}
                className="chibi-checkbox"
                data-testid={`list-check-${it.id}`}
              />
              <div className="flex-1 min-w-0">
                <div className={`font-medium ${it.checked ? "line-through text-[#4A3F35]/40" : "text-[#4A3F35]"}`}>
                  {it.name}
                </div>
                {it.recipes.length > 0 && (
                  <div className="text-xs text-[#4A3F35]/60 mt-0.5 flex flex-wrap items-center gap-1">
                    <ChefHat className="inline w-3 h-3" />
                    {it.recipes.map((r, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => onOpenRecipe(r)}
                        className="px-2 py-0.5 rounded-full bg-[#A3B18A]/15 hover:bg-[#A3B18A]/30 text-[#4A3F35] transition-colors"
                        data-testid={`recipe-pill-${it.id}-${idx}`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <input
                type="number"
                step="0.1"
                value={it.quantity}
                onChange={(e) => onEditQty(it.id, e.target.value)}
                className="w-20 px-3 py-1.5 rounded-full border border-[#E8E2D2] text-sm text-right focus:border-[#A3B18A] focus:outline-none"
                data-testid={`list-qty-${it.id}`}
              />
              <select
                value={it.unit}
                onChange={(e) => onChangeUnit(it.id, e.target.value)}
                className="text-xs px-2 py-1.5 rounded-full border border-[#E8E2D2] bg-white focus:border-[#A3B18A] focus:outline-none"
              >
                {UNITS.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
              <button
                onClick={() => onDelete(it.id)}
                className="p-1.5 rounded-full hover:bg-[#E07A5F]/10 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`list-delete-${it.id}`}
              >
                <Trash2 className="w-4 h-4 text-[#E07A5F]" />
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export default function ShoppingListPage() {
  const navigate = useNavigate();
  const [list, setList] = useState({ items: [], share_id: null });
  const [loading, setLoading] = useState(true);
  const [manualOpen, setManualOpen] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverRayon, setDragOverRayon] = useState(null);
  const [recipeModal, setRecipeModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.getList();
      setList(data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (id, checked) => {
    setList((l) => ({ ...l, items: l.items.map((i) => (i.id === id ? { ...i, checked } : i)) }));
    try { await api.updateItem(id, { checked }); } catch { load(); }
  };

  const handleEditQty = async (id, val) => {
    const q = parseFloat(val);
    setList((l) => ({ ...l, items: l.items.map((i) => (i.id === id ? { ...i, quantity: val } : i)) }));
    if (!isNaN(q)) {
      try { await api.updateItem(id, { quantity: q }); } catch { load(); }
    }
  };

  const handleChangeUnit = async (id, unit) => {
    setList((l) => ({ ...l, items: l.items.map((i) => (i.id === id ? { ...i, unit } : i)) }));
    try { await api.updateItem(id, { unit }); } catch { load(); }
  };

  const handleDelete = async (id) => {
    try {
      const data = await api.deleteItem(id);
      setList(data);
      toast.success("Supprimé");
    } catch { toast.error("Erreur"); }
  };

  const [shareText, setShareText] = useState(null);

  const handleReset = async () => {
    try {
      await api.resetList();
      toast.success("Nouvelle semaine ! 🌱");
      navigate("/import");
    } catch { toast.error("Erreur"); }
  };

  const handleShare = () => {
    if (list.items.length === 0) {
      toast.error("Liste vide");
      return;
    }
    const lines = [];
    RAYONS.forEach((r) => {
      const items = list.items.filter((i) => i.rayon === r);
      if (items.length === 0) return;
      const meta = RAYON_META[r];
      lines.push(`${meta.emoji} ${r}`);
      items.forEach((it) => {
        const recipes = it.recipes.length ? ` (${it.recipes.join(", ")})` : "";
        const checkbox = it.checked ? "☑" : "☐";
        lines.push(`${checkbox} ${it.name} : ${it.quantity} ${it.unit}${recipes}`);
      });
      lines.push("");
    });
    const text = lines.join("\n").trim();
    setShareText(text);
  };

  const handleDrop = async (targetRayon) => {
    if (!draggedId) return;
    const item = list.items.find((i) => i.id === draggedId);
    if (!item || item.rayon === targetRayon) {
      setDraggedId(null);
      return;
    }
    setList((l) => ({ ...l, items: l.items.map((i) => (i.id === draggedId ? { ...i, rayon: targetRayon } : i)) }));
    setDraggedId(null);
    try { await api.updateItem(item.id, { rayon: targetRayon }); } catch { load(); }
  };

  const grouped = RAYONS.reduce((acc, r) => {
    acc[r] = list.items.filter((i) => i.rayon === r);
    return acc;
  }, {});

  const totalItems = list.items.length;
  const doneItems = list.items.filter((i) => i.checked).length;

  return (
    <main className="min-h-screen w-full px-4 md:px-6 py-8" data-testid={LIST_IDS.container}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <header className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <button
              data-testid={LIST_IDS.homeBtn}
              onClick={() => navigate("/")}
              className="chibi-btn-ghost !py-2 !px-3"
            >
              <HomeIcon className="w-4 h-4" />
            </button>
            <div>
              <h1 className="font-display font-semibold text-3xl md:text-4xl tracking-tight">
                Ma liste de <span style={{ color: "#E07A5F" }}>courses</span>
              </h1>
              <p className="text-sm text-[#4A3F35]/60 mt-1">
                {doneItems}/{totalItems} article{totalItems > 1 ? "s" : ""} cochés
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              data-testid={LIST_IDS.shareBtn}
              onClick={handleShare}
              className="chibi-btn-ghost inline-flex items-center gap-2"
            >
              <Share2 className="w-4 h-4" /> Partager
            </button>
          </div>
        </header>

        {loading ? (
          <div className="text-center py-20 font-soft text-[#4A3F35]/60">Chargement... 🐰</div>
        ) : totalItems === 0 ? (
          <div className="chibi-card p-12 text-center chibi-pop">
            <div className="text-6xl mb-4">🧺</div>
            <h2 className="font-display font-semibold text-2xl mb-2">Ta liste est vide</h2>
            <p className="text-[#4A3F35]/60 mb-6">Ajoute ta première recette pour commencer</p>
            <button onClick={() => navigate("/import")} className="chibi-btn-primary inline-flex items-center gap-2">
              <Plus className="w-5 h-5" /> Ajouter une recette
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {RAYONS.map((r) => (
              <RayonBlock
                key={r}
                rayon={r}
                items={grouped[r]}
                onDrop={handleDrop}
                onDragStart={(id) => setDraggedId(id)}
                dragOverRayon={dragOverRayon}
                setDragOverRayon={setDragOverRayon}
                onToggle={handleToggle}
                onEditQty={handleEditQty}
                onDelete={handleDelete}
                onChangeUnit={handleChangeUnit}
                onOpenRecipe={setRecipeModal}
              />
            ))}
          </div>
        )}

        {/* Bottom actions */}
        <footer className="mt-10 flex flex-wrap gap-3 justify-center pb-8">
          <button
            data-testid={LIST_IDS.addRecipeBtn}
            onClick={() => navigate("/import")}
            className="chibi-btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" /> Ajouter une recette
          </button>
          <button
            data-testid={LIST_IDS.addItemBtn}
            onClick={() => setManualOpen(true)}
            className="chibi-btn-secondary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Ajouter un ingrédient
          </button>
          <button
            data-testid={LIST_IDS.resetBtn}
            onClick={handleReset}
            className="chibi-btn-ghost inline-flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Nouvelle semaine
          </button>
        </footer>
      </div>

      {manualOpen && (
        <ManualEntryModal
          onClose={() => setManualOpen(false)}
          onDone={() => {
            setManualOpen(false);
            load();
          }}
        />
      )}

      {recipeModal && (
        <RecipeModal
          recipeName={recipeModal}
          items={list.items.filter((i) => i.recipes.includes(recipeModal))}
          onClose={() => setRecipeModal(null)}
        />
      )}

      {shareText !== null && (
        <ShareModal text={shareText} onClose={() => setShareText(null)} />
      )}
    </main>
  );
}

function ShareModal({ text, onClose }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copié dans le presse-papier ! 📋");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Sélectionne le texte et copie manuellement");
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="share-modal"
    >
      <div
        className="chibi-card w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col chibi-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-[#E8E2D2]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#A3B18A" }}>
              <Share2 className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="font-display font-semibold text-xl text-[#4A3F35]">Partager ma liste</h3>
              <p className="text-xs text-[#4A3F35]/60">Copie ce texte et colle-le dans Notes iOS 📱</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[#F4F1EA] flex-shrink-0"
            data-testid="share-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 flex-1 overflow-y-auto">
          <textarea
            readOnly
            value={text}
            onFocus={(e) => e.target.select()}
            className="w-full h-72 p-4 rounded-[24px] border-2 border-[#E8E2D2] bg-[#FDFBF7] font-mono text-sm text-[#4A3F35] focus:border-[#A3B18A] focus:outline-none resize-none"
            data-testid="share-modal-textarea"
          />
        </div>
        <div className="p-6 border-t border-[#E8E2D2] flex flex-col sm:flex-row gap-3 justify-end">
          <button onClick={onClose} className="chibi-btn-ghost" data-testid="share-modal-cancel">
            Fermer
          </button>
          <button
            onClick={handleCopy}
            className="chibi-btn-primary inline-flex items-center gap-2 justify-center"
            data-testid="share-modal-copy"
          >
            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            {copied ? "Copié !" : "Copier le texte"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RecipeModal({ recipeName, items, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="recipe-modal"
    >
      <div
        className="chibi-card w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col chibi-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-[#E8E2D2]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#E07A5F" }}>
              <ChefHat className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase font-soft tracking-wide text-[#4A3F35]/60">Recette</p>
              <h3 className="font-display font-semibold text-xl text-[#4A3F35] truncate">{recipeName}</h3>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F4F1EA] flex-shrink-0" data-testid="recipe-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {items.length === 0 ? (
            <p className="text-center text-[#4A3F35]/50 italic">Aucun ingrédient</p>
          ) : (
            <ul className="space-y-2">
              {items.map((it) => {
                const meta = RAYON_META[it.rayon] || RAYON_META["Autre"];
                return (
                  <li
                    key={it.id}
                    className="flex items-center gap-3 p-3 rounded-[20px] border border-[#E8E2D2]"
                    style={{ background: meta.bg }}
                  >
                    <span className="text-xl">{meta.emoji}</span>
                    <span className="flex-1 font-medium">{it.name}</span>
                    <span className="text-sm text-[#4A3F35]/70">
                      {it.quantity} {it.unit}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="p-4 border-t border-[#E8E2D2] text-center">
          <p className="text-xs text-[#4A3F35]/50">{items.length} ingrédient{items.length > 1 ? "s" : ""} dans cette recette</p>
        </div>
      </div>
    </div>
  );
}
